import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { addDays, differenceInCalendarDays, getDate, parseISO } from 'date-fns';
import { observer } from 'mobx-react';
import {
  paymentMethod,
  paymentType,
  originalGSNICStates,
  onlyPayInFullOnAutoPoliciesStates,
  soldToEffectiveDaysIntervalByState,
  policyType as PolicyTypes,
  advanceQuoteDaysStates
} from '@ourbranch/lookups';

import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { FormField } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import { canOfferMegaDownPay, isMegaDownPayRequired } from '../../helpers/mega-down-pay-helpers';
import { FutureEffectiveDateWarning } from './future-effective-date-warning';
import { MegaDownPayCard } from './mega-down-pay-card';
import { SmallBillingNote } from './small-billing-note';
import useStyles from './settings.styles';

const AutoSettings = observer(function AutoSettings({ paymentMethodOptions, billingDaysOptions, paymentTypeOptions }) {
  const { offer: store } = useStore();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { isBix } = store;
  const state = store?.state;

  const daysOut = soldToEffectiveDaysIntervalByState(state, PolicyTypes.Auto);
  const showSmallBillingNote =
    values.global.autoPaymentMethod === paymentMethod.ACH && !isBix && originalGSNICStates.includes(state);
  const isMIMonolineAuto = state === 'MI' && values.selectedOption === PolicyTypes.Auto;
  const onlyPayInFull = onlyPayInFullOnAutoPoliciesStates[state] || isMIMonolineAuto;

  const offerMegaDownPay = useMemo(
    () =>
      canOfferMegaDownPay(
        values.global.autoEffectiveDate,
        values.global.autoBillingDayOfMonth,
        values.global.autoPaymentType
      ),
    [values.global.autoEffectiveDate, values.global.autoBillingDayOfMonth, values.global.autoPaymentType]
  );

  const megaDownPayRequired = useMemo(
    () =>
      isMegaDownPayRequired(
        values.global.autoEffectiveDate,
        values.global.autoBillingDayOfMonth,
        values.global.autoPaymentType
      ),
    [values.global.autoEffectiveDate, values.global.autoBillingDayOfMonth, values.global.autoPaymentType]
  );

  // Calculate days until effective for AQD states
  const isAQDState = !!advanceQuoteDaysStates[state]?.[PolicyTypes.Auto];
  const creationDate = store.offer?.inheritedInitialCreationDateTime
    ? parseISO(store.offer.inheritedInitialCreationDateTime)
    : store.offer?.initialCreationDateTime
      ? parseISO(store.offer.initialCreationDateTime)
      : undefined;

  const effectiveDate = values.global?.autoEffectiveDate ? parseISO(values.global.autoEffectiveDate) : null;

  const autoDaysUntilEffective =
    creationDate && effectiveDate ? differenceInCalendarDays(effectiveDate, creationDate) : null;

  // this is used to default the billing day of the month to the day that the policy is effective
  const autoEffectiveDay = useMemo(() => {
    if (effectiveDate) {
      return getDate(effectiveDate);
    }
  }, [effectiveDate]);

  // if auto billing day of month is null, default select the effective day of the policy
  // or the 28th if the effectiveDay is past the allowed options of days 1 - 28
  useEffect(() => {
    if (!values.global.autoBillingDayOfMonth && values.global.autoBillingDayOfMonth !== autoEffectiveDay) {
      if (autoEffectiveDay > 28) {
        setFieldValue('global.autoBillingDayOfMonth', 28);
      } else {
        setFieldValue('global.autoBillingDayOfMonth', autoEffectiveDay);
      }
    }
  }, [values.global.autoBillingDayOfMonth, autoEffectiveDay, setFieldValue]);

  return (
    <Section title="Auto Policy Settings" type="SubSection">
      <Card className={classes.container} type="secondary" id="autoSettings">
        <Label className={classes.subtitle} type="formSubTitle">
          Policy Term
        </Label>
        <Grid container>
          <FormField
            type="date"
            id="global.autoEffectiveDate"
            name="global.autoEffectiveDate"
            label="Auto policy effective date"
            xs={4}
            mode="dark"
            disableFuture={false}
            minDate={addDays(new Date(), daysOut)}
            maxDate={addDays(new Date(), 60)}
            fast={false}
          />
          <Grid item xs={12}>
            <FutureEffectiveDateWarning
              daysOut={daysOut}
              isAQDState={isAQDState}
              daysUntilEffective={autoDaysUntilEffective}
              creationDate={creationDate}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className={classes.billingDetails} alignItems="center">
          <Grid item xs={2}>
            <Label type="formSubTitle" className={classes.subtitle}>
              Billing Details
            </Label>
          </Grid>
          <SmallBillingNote show={showSmallBillingNote} />
        </Grid>
        <Grid container>
          <Grid container item xs={12} spacing={4}>
            <FormField
              label="Auto payment method"
              xs={4}
              type="select"
              name="global.autoPaymentMethod"
              options={paymentMethodOptions}
              mode="dark"
            />
            {onlyPayInFull && (
              <Grid item xs={4}>
                <LabelTooltip
                  label="Auto payment frequency"
                  tooltip={{
                    label: 'More Info',
                    onHoverText: isMIMonolineAuto
                      ? 'Monoline auto policies are not eligible to be paid monthly in Michigan.'
                      : 'Auto policies are not eligible to be paid monthly in this state.'
                  }}
                >
                  <FormField
                    type="select"
                    name="global.autoPaymentType"
                    options={paymentTypeOptions}
                    disabled={onlyPayInFull}
                    mode="dark"
                    fast={false}
                  />
                </LabelTooltip>
              </Grid>
            )}
            {!onlyPayInFull && (
              <FormField
                label="Auto payment frequency"
                xs={4}
                type="select"
                name="global.autoPaymentType"
                options={paymentTypeOptions}
                mode="dark"
                fast={false}
              />
            )}
            {values.global?.autoPaymentType === paymentType.Monthly && (
              <FormField
                type="select"
                name="global.autoBillingDayOfMonth"
                label="Auto Billing Day"
                mode="dark"
                options={billingDaysOptions}
                xs={4}
                fast={false}
              />
            )}
          </Grid>
        </Grid>
        {(offerMegaDownPay || megaDownPayRequired) && (
          <MegaDownPayCard megaDownPayRequired={megaDownPayRequired} formikValue="global.autoMegaDownPay" />
        )}
      </Card>
    </Section>
  );
});

AutoSettings.propTypes = {
  paymentMethodOptions: PropTypes.array.isRequired,
  billingDaysOptions: PropTypes.array.isRequired,
  paymentTypeOptions: PropTypes.array.isRequired
};

export default withDatePicker(AutoSettings);
